import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Save, PictureAsPdf } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { sessionAPI, studentAPI, transportationAPI } from '../../services/api';
import AppHeader from '../AppHeader';

const PROGRAM_TYPES = [
  { value: 'EI', label: 'Early Intervention' },
  { value: 'PRESCHOOL', label: 'Preschool' },
  { value: 'ESY', label: 'ESY' },
  { value: 'SCHOOL_YEAR', label: 'School Year' },
];

const STATUS_LABELS = { DRAFT: 'Draft', SENT: 'Sent to Parent', SIGNED: 'Signed', COMPLETE: 'Complete' };

const emptyForm = () => ({
  program_type: '',
  school_year_start: null,
  school_year_end: null,
  medical_alert: false,
  medical_alert_details: '',
  wheelchair_bus_required: false,
  travels_with_nurse: false,
  travels_with_oxygen: false,
  safety_apparatus_required: false,
  unable_to_sit_upright: false,
  ei_hub_id: '',
  cpse_gen_ed_id: '',
  ifsp_cpse_meeting_date: '',
  days_attending_mon: false,
  days_attending_tue: false,
  days_attending_wed: false,
  days_attending_thu: false,
  days_attending_fri: false,
  session_time_from: '',
  session_time_to: '',
  requested_start_date: '',
  terminate_transportation_date: '',
  change_school_district: false,
  change_session_time: false,
  change_home_address: false,
  change_phone_number: false,
  change_pickup_address: false,
  change_dropoff_address: false,
  emergency_contact_1_name: '',
  emergency_contact_1_phone: '',
  emergency_contact_1_relationship: '',
  emergency_contact_2_name: '',
  emergency_contact_2_phone: '',
  emergency_contact_2_relationship: '',
  do_not_release_1: '',
  do_not_release_2: '',
  do_not_release_3: '',
  pickup_days_mon: false,
  pickup_days_tue: false,
  pickup_days_wed: false,
  pickup_days_thu: false,
  pickup_days_fri: false,
  pickup_daycare_name: '',
  pickup_address: '',
  pickup_city: '',
  pickup_zip: '',
  pickup_cross_streets: '',
  pickup_contact_name: '',
  pickup_contact_phone: '',
  dropoff_days_mon: false,
  dropoff_days_tue: false,
  dropoff_days_wed: false,
  dropoff_days_thu: false,
  dropoff_days_fri: false,
  dropoff_daycare_name: '',
  dropoff_address: '',
  dropoff_city: '',
  dropoff_zip: '',
  dropoff_cross_streets: '',
  dropoff_contact_name: '',
  dropoff_contact_phone: '',
  status: 'DRAFT',
  ghl_document_id: '',
});

const toPayload = (form, studentId, sessionId, siteId) => {
  const p = { ...form, student: studentId, session: sessionId, site: siteId };
  ['session_time_from', 'session_time_to'].forEach((k) => {
    if (p[k] === '' || p[k] == null) p[k] = null;
    else if (typeof p[k] === 'string') p[k] = p[k].slice(0, 5);
  });
  ['requested_start_date', 'terminate_transportation_date', 'ifsp_cpse_meeting_date'].forEach((k) => {
    if (p[k] === '') p[k] = null;
  });
  return p;
};

const TransportationForm = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const isNew = studentId === 'new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [trfId, setTrfId] = useState(null);
  const [student, setStudent] = useState(null);
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState(emptyForm());

  const currentStudentId = isNew ? student?.id : (studentId ? Number(studentId) : null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let sessionRes = null;
      try {
        sessionRes = await sessionAPI.getCurrentSession();
      } catch (_) {
        const sessRes = await sessionAPI.getSessions();
        const list = sessRes.data.results || sessRes.data || [];
        if (list.length > 0) {
          await sessionAPI.setActiveSession(list[0].id);
          sessionRes = await sessionAPI.getCurrentSession();
        }
      }
      const sess = sessionRes?.data;
      setSession(sess);

      if (isNew) {
        const stuRes = await studentAPI.getStudents(sess?.id);
        const list = stuRes.data?.results || stuRes.data || [];
        setStudents(Array.isArray(list) ? list : []);
        setForm(emptyForm());
        setTrfId(null);
        setStudent(null);
        setLoading(false);
        return;
      }

      const sid = Number(studentId);
      const [trfListRes, stuRes] = await Promise.all([
        transportationAPI.list({ student: sid }),
        studentAPI.getStudent(sid),
      ]);
      const trfList = trfListRes.data.results || trfListRes.data || [];
      const trf = trfList[0] || null;
      const stu = stuRes.data;

      setStudent(stu);
      if (trf) {
        setTrfId(trf.id);
        const f = emptyForm();
        Object.keys(f).forEach((k) => {
          if (trf[k] !== undefined) f[k] = trf[k];
          if (typeof f[k] === 'string' && (trf[k] === null || trf[k] === undefined)) f[k] = '';
          if (['session_time_from', 'session_time_to'].includes(k) && trf[k]) {
            f[k] = String(trf[k]).slice(0, 5);
          }
        });
        setForm(f);
      } else {
        const prefillRes = await transportationAPI.prefill(sid);
        const pre = prefillRes.data;
        setForm((prev) => ({
          ...prev,
          student: pre.student,
          session: pre.session,
          site: pre.site,
          program_type: pre.program_type || 'SCHOOL_YEAR',
          school_year_start: pre.school_year_start ?? null,
          school_year_end: pre.school_year_end ?? null,
        }));
        setTrfId(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [studentId, isNew]);

  useEffect(() => {
    if (!isNew && studentId) loadData();
    else if (isNew) loadData();
  }, [loadData, isNew, studentId]);

  const loadPrefill = async (sid) => {
    try {
      const res = await transportationAPI.prefill(sid);
      const pre = res.data;
      const stuRes = await studentAPI.getStudent(sid);
      setStudent(stuRes.data);
      setForm((prev) => ({
        ...prev,
        student: pre.student,
        session: pre.session,
        site: pre.site,
        program_type: pre.program_type || 'SCHOOL_YEAR',
        school_year_start: pre.school_year_start ?? null,
        school_year_end: pre.school_year_end ?? null,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Prefill failed');
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async () => {
    const sessionId = form.session ?? session?.id;
    const siteId = form.site ?? session?.site_id ?? session?.site?.id ?? session?.site;
    if (!currentStudentId || !sessionId || !siteId) {
      setError('Missing student or session. Select a student and ensure session is loaded.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = toPayload(form, currentStudentId, sessionId, siteId);
      if (trfId) {
        await transportationAPI.update(trfId, payload);
      } else {
        const res = await transportationAPI.create(payload);
        setTrfId(res.data.id);
      }
      setTimeout(() => navigate('/transportation'), 0);
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!trfId) {
      setError('Save draft first, then generate PDF.');
      return;
    }
    setError('');
    try {
      const res = await transportationAPI.downloadPdf(trfId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TRF_${student?.last_name || 'student'}_${student?.first_name || 'unknown'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate PDF');
    }
  };

  const handleDownloadCountyTrf = async () => {
    if (!trfId) {
      setError('Save draft first, then download the county TRF.');
      return;
    }
    setError('');
    try {
      const res = await transportationAPI.downloadCountyTrf(trfId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `County_TRF_${student?.last_name || 'student'}_${student?.first_name || 'unknown'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate county TRF PDF');
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box>
      <AppHeader backTo="/transportation" />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h5">Transportation Request Form</Typography>
            {form.status && form.status !== 'DRAFT' && (
              <Chip label={STATUS_LABELS[form.status] || form.status} color="primary" size="small" />
            )}
          </Box>

          {isNew && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Student</InputLabel>
                <Select
                  value={student?.id ?? ''}
                  label="Student"
                  onChange={(e) => {
                    const id = e.target.value;
                    if (id) loadPrefill(id);
                  }}
                >
                  <MenuItem value="">Select student</MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.last_name}, {s.first_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {!isNew && student && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Student: {student.last_name}, {student.first_name}
            </Typography>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Box 1 — Program &amp; medical</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Program type</InputLabel>
                  <Select
                    value={form.program_type || ''}
                    label="Program type"
                    onChange={(e) => handleChange('program_type', e.target.value)}
                  >
                    {PROGRAM_TYPES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField size="small" label="School year start" type="number" value={form.school_year_start ?? ''} onChange={(e) => handleChange('school_year_start', e.target.value ? parseInt(e.target.value, 10) : null)} sx={{ width: 120 }} />
                <TextField size="small" label="School year end" type="number" value={form.school_year_end ?? ''} onChange={(e) => handleChange('school_year_end', e.target.value ? parseInt(e.target.value, 10) : null)} sx={{ width: 120 }} />
                <FormControlLabel control={<Checkbox checked={form.medical_alert} onChange={(e) => handleChange('medical_alert', e.target.checked)} />} label="Medical alert" />
              </Box>
              {form.medical_alert && (
                <TextField fullWidth size="small" label="Medical alert details" value={form.medical_alert_details} onChange={(e) => handleChange('medical_alert_details', e.target.value)} multiline rows={2} sx={{ mt: 1 }} />
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 4 — IDs</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <TextField size="small" label="EI HUB ID" value={form.ei_hub_id} onChange={(e) => handleChange('ei_hub_id', e.target.value)} />
                <TextField size="small" label="CPSE Gen Ed ID" value={form.cpse_gen_ed_id} onChange={(e) => handleChange('cpse_gen_ed_id', e.target.value)} />
                <TextField size="small" label="IFSP/CPSE meeting date" type="date" value={form.ifsp_cpse_meeting_date ? String(form.ifsp_cpse_meeting_date).slice(0, 10) : ''} onChange={(e) => handleChange('ifsp_cpse_meeting_date', e.target.value || null)} InputLabelProps={{ shrink: true }} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Schedule</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {['mon', 'tue', 'wed', 'thu', 'fri'].map((d) => (
                  <FormControlLabel key={d} control={<Checkbox checked={form[`days_attending_${d}`]} onChange={(e) => handleChange(`days_attending_${d}`, e.target.checked)} />} label={d.charAt(0).toUpperCase() + d.slice(1)} />
                ))}
                <TextField size="small" label="Session from" type="time" value={form.session_time_from || ''} onChange={(e) => handleChange('session_time_from', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
                <TextField size="small" label="Session to" type="time" value={form.session_time_to || ''} onChange={(e) => handleChange('session_time_to', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Dates</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <TextField size="small" label="Requested start date" type="date" value={form.requested_start_date ? String(form.requested_start_date).slice(0, 10) : ''} onChange={(e) => handleChange('requested_start_date', e.target.value || null)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" label="Terminate transportation date" type="date" value={form.terminate_transportation_date ? String(form.terminate_transportation_date).slice(0, 10) : ''} onChange={(e) => handleChange('terminate_transportation_date', e.target.value || null)} InputLabelProps={{ shrink: true }} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 3 — Change in information</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {['change_school_district', 'change_session_time', 'change_home_address', 'change_phone_number', 'change_pickup_address', 'change_dropoff_address'].map((k) => (
                  <FormControlLabel key={k} control={<Checkbox checked={form[k]} onChange={(e) => handleChange(k, e.target.checked)} />} label={k.replace('change_', '').replace(/_/g, ' ')} />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 5 — Special needs</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {['wheelchair_bus_required', 'travels_with_nurse', 'travels_with_oxygen', 'safety_apparatus_required', 'unable_to_sit_upright'].map((k) => (
                  <FormControlLabel key={k} control={<Checkbox checked={form[k]} onChange={(e) => handleChange(k, e.target.checked)} />} label={k.replace(/_/g, ' ')} />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 7 — Emergency contact 1</Typography>
              <TextField fullWidth size="small" label="Name" value={form.emergency_contact_1_name} onChange={(e) => handleChange('emergency_contact_1_name', e.target.value)} sx={{ mt: 1 }} />
              <TextField fullWidth size="small" label="Phone" value={form.emergency_contact_1_phone} onChange={(e) => handleChange('emergency_contact_1_phone', e.target.value)} />
              <TextField fullWidth size="small" label="Relationship" value={form.emergency_contact_1_relationship} onChange={(e) => handleChange('emergency_contact_1_relationship', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Emergency contact 2</Typography>
              <TextField fullWidth size="small" label="Name" value={form.emergency_contact_2_name} onChange={(e) => handleChange('emergency_contact_2_name', e.target.value)} sx={{ mt: 1 }} />
              <TextField fullWidth size="small" label="Phone" value={form.emergency_contact_2_phone} onChange={(e) => handleChange('emergency_contact_2_phone', e.target.value)} />
              <TextField fullWidth size="small" label="Relationship" value={form.emergency_contact_2_relationship} onChange={(e) => handleChange('emergency_contact_2_relationship', e.target.value)} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 8 — Do not release to</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <TextField size="small" label="1" value={form.do_not_release_1} onChange={(e) => handleChange('do_not_release_1', e.target.value)} />
                <TextField size="small" label="2" value={form.do_not_release_2} onChange={(e) => handleChange('do_not_release_2', e.target.value)} />
                <TextField size="small" label="3" value={form.do_not_release_3} onChange={(e) => handleChange('do_not_release_3', e.target.value)} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 9 — Pickup location</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {['mon', 'tue', 'wed', 'thu', 'fri'].map((d) => (
                  <FormControlLabel key={d} control={<Checkbox checked={form[`pickup_days_${d}`]} onChange={(e) => handleChange(`pickup_days_${d}`, e.target.checked)} />} label={d} />
                ))}
              </Box>
              <TextField fullWidth size="small" label="Daycare name" value={form.pickup_daycare_name} onChange={(e) => handleChange('pickup_daycare_name', e.target.value)} sx={{ mt: 1 }} />
              <TextField fullWidth size="small" label="Address" value={form.pickup_address} onChange={(e) => handleChange('pickup_address', e.target.value)} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField size="small" label="City" value={form.pickup_city} onChange={(e) => handleChange('pickup_city', e.target.value)} />
                <TextField size="small" label="Zip" value={form.pickup_zip} onChange={(e) => handleChange('pickup_zip', e.target.value)} />
                <TextField size="small" label="Cross streets" value={form.pickup_cross_streets} onChange={(e) => handleChange('pickup_cross_streets', e.target.value)} />
                <TextField size="small" label="Contact name" value={form.pickup_contact_name} onChange={(e) => handleChange('pickup_contact_name', e.target.value)} />
                <TextField size="small" label="Contact phone" value={form.pickup_contact_phone} onChange={(e) => handleChange('pickup_contact_phone', e.target.value)} />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Box 10 — Dropoff location</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {['mon', 'tue', 'wed', 'thu', 'fri'].map((d) => (
                  <FormControlLabel key={d} control={<Checkbox checked={form[`dropoff_days_${d}`]} onChange={(e) => handleChange(`dropoff_days_${d}`, e.target.checked)} />} label={d} />
                ))}
              </Box>
              <TextField fullWidth size="small" label="Daycare name" value={form.dropoff_daycare_name} onChange={(e) => handleChange('dropoff_daycare_name', e.target.value)} sx={{ mt: 1 }} />
              <TextField fullWidth size="small" label="Address" value={form.dropoff_address} onChange={(e) => handleChange('dropoff_address', e.target.value)} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField size="small" label="City" value={form.dropoff_city} onChange={(e) => handleChange('dropoff_city', e.target.value)} />
                <TextField size="small" label="Zip" value={form.dropoff_zip} onChange={(e) => handleChange('dropoff_zip', e.target.value)} />
                <TextField size="small" label="Cross streets" value={form.dropoff_cross_streets} onChange={(e) => handleChange('dropoff_cross_streets', e.target.value)} />
                <TextField size="small" label="Contact name" value={form.dropoff_contact_name} onChange={(e) => handleChange('dropoff_contact_name', e.target.value)} />
                <TextField size="small" label="Contact phone" value={form.dropoff_contact_phone} onChange={(e) => handleChange('dropoff_contact_phone', e.target.value)} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<Save />} onClick={handleSaveDraft} disabled={saving || !currentStudentId}>
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={handleDownloadPdf} disabled={!trfId}>
              Download PDF
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdf />}
              onClick={handleDownloadCountyTrf}
              disabled={!trfId}
            >
              Download county TRF (filled)
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TransportationForm;
